import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    CircularProgress,
} from '@mui/material';
import { createFolder, renameFolder, renameDocument } from '../services/closeoutDocsService';

/**
 * ItemNameDialog Component
 * Unified dialog for creating folders or renaming folders/documents
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Callback to close the dialog
 * @param {Function} onSuccess - Callback called on successful operation
 * @param {string} projectId - The UUID of the project
 * @param {string} mode - 'createFolder', 'renameFolder', or 'renameDocument'
 * @param {string|null} parentFolderId - The UUID of the parent folder (null for root) - used for createFolder mode
 * @param {Object|null} item - The folder or document object to rename (null for create mode)
 */
const ItemNameDialog = ({
    open,
    onClose,
    onSuccess,
    projectId,
    mode = 'createFolder',
    parentFolderId = null,
    item = null,
}) => {
    const [itemName, setItemName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Determine dialog configuration based on mode
    const config = {
        createFolder: {
            title: 'Create New Folder',
            label: 'Folder Name',
            placeholder: 'Enter folder name',
            buttonText: 'Create',
            successMessage: 'Folder created successfully',
        },
        renameFolder: {
            title: 'Rename Folder',
            label: 'Folder Name',
            placeholder: 'Enter folder name',
            buttonText: 'Rename',
            successMessage: 'Folder renamed successfully',
        },
        renameDocument: {
            title: 'Rename File',
            label: 'File Name',
            placeholder: 'Enter file name',
            buttonText: 'Rename',
            successMessage: 'File renamed successfully',
        },
    }[mode];

    // Update item name when item changes
    useEffect(() => {
        if (mode === 'renameFolder' && item) {
            setItemName(item.folderName || '');
            setError('');
        } else if (mode === 'renameDocument' && item) {
            setItemName(item.originalFilename || '');
            setError('');
        } else if (mode === 'createFolder') {
            setItemName('');
            setError('');
        }
    }, [item, mode]);

    const handleClose = () => {
        if (!loading) {
            setItemName('');
            setError('');
            onClose();
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!itemName.trim()) {
            setError(`${config.label} is required`);
            return;
        }

        // For rename modes, check if name changed
        if (mode === 'renameFolder' && itemName.trim() === item?.folderName) {
            setError('Please enter a different name');
            return;
        }

        if (mode === 'renameDocument' && itemName.trim() === item?.originalFilename) {
            setError('Please enter a different name');
            return;
        }

        setLoading(true);
        setError('');

        try {
            if (mode === 'createFolder') {
                await createFolder({
                    projectId,
                    folderName: itemName.trim(),
                    parentFolderId,
                });
            } else if (mode === 'renameFolder') {
                await renameFolder(item.folderId, projectId, itemName.trim());
            } else if (mode === 'renameDocument') {
                await renameDocument(item.documentId, projectId, itemName.trim());
            }

            // Success
            setItemName('');
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    `Failed to ${mode === 'createFolder' ? 'create folder' : 'rename item'}`
            );
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !loading) {
            handleSubmit();
        }
    };

    const isSubmitDisabled = () => {
        if (loading || !itemName.trim()) return true;
        if (mode === 'renameFolder' && itemName.trim() === item?.folderName) return true;
        if (mode === 'renameDocument' && itemName.trim() === item?.originalFilename) return true;
        return false;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 600 }}>{config.title}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        label={config.label}
                        variant="outlined"
                        value={itemName}
                        onChange={(e) => setItemName(e.target.value)}
                        onKeyPress={handleKeyPress}
                        error={Boolean(error)}
                        helperText={error}
                        disabled={loading}
                        placeholder={config.placeholder}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSubmitDisabled()}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {loading ? <CircularProgress size={24} /> : config.buttonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ItemNameDialog;
